import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getRecentColors, pushRecentColor, updateBrandKit, useBrandKit } from "./brandKit.js";

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

// Colours already used in the open template, provided by the editor so every
// picker can offer them ("In this template").
export const TemplateColorsContext = createContext([]);

// Swatch + typable hex field. The swatch opens a palette popover: the brand
// kit first (editable, shared by the whole workspace), then this template's
// colours, recent picks, and a full custom picker with an eyedropper.
//
// The hex field is buffered as local text rather than driven straight off
// `value` — it needs to hold an incomplete value ("#3", "#33a") while the
// user is mid-typing without either reverting early or committing a bad
// color. onChange only fires once the text is a complete 6-digit hex.
export default function ColorInput({ value, onChange, title, className = "" }) {
  const safeValue = HEX_RE.test(value) ? value : "#000000";
  const [hexText, setHexText] = useState(safeValue);
  const [open, setOpen] = useState(false);
  const swatchRef = useRef(null);

  useEffect(() => {
    setHexText(HEX_RE.test(value) ? value : safeValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const commit = (text) => {
    const hex = text.startsWith("#") ? text : `#${text}`;
    if (HEX_RE.test(hex)) onChange(hex.toLowerCase());
  };

  return (
    <div className={`color-input ${className}`} title={title}>
      <button
        type="button"
        ref={swatchRef}
        className="color-input-swatch"
        style={{ background: safeValue }}
        onClick={() => setOpen((o) => !o)}
        aria-label="Choose colour"
      />
      <input
        type="text"
        className="color-input-hex"
        value={hexText}
        maxLength={7}
        spellCheck={false}
        onChange={(e) => {
          setHexText(e.target.value);
          commit(e.target.value);
        }}
        onBlur={() => {
          setHexText(safeValue);
          if (HEX_RE.test(safeValue)) pushRecentColor(safeValue.toLowerCase());
        }}
      />
      {open && (
        <ColorPopover
          anchorRef={swatchRef}
          value={safeValue}
          onPick={(hex) => {
            onChange(hex.toLowerCase());
            pushRecentColor(hex.toLowerCase());
          }}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

function Swatches({ colors, value, onPick, onRemove }) {
  return (
    <div className="cp-swatches">
      {colors.map((c) => (
        <span key={c} className="cp-swatch-wrap">
          <button
            type="button"
            className={`cp-swatch ${c.toLowerCase() === value.toLowerCase() ? "on" : ""}`}
            style={{ background: c }}
            title={c}
            onClick={() => onPick(c)}
          />
          {onRemove && (
            <button type="button" className="cp-swatch-x" title="Remove from brand kit" onClick={() => onRemove(c)}>
              ×
            </button>
          )}
        </span>
      ))}
    </div>
  );
}

function ColorPopover({ anchorRef, value, onPick, onClose }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ left: -9999, top: -9999 });
  const [editing, setEditing] = useState(false);
  const kit = useBrandKit();
  const templateColors = useContext(TemplateColorsContext);
  const recent = getRecentColors();

  // Fixed-position next to the swatch, flipped/clamped to stay on screen
  // (pickers live in scrolling side panels that would clip an absolute one).
  useLayoutEffect(() => {
    const a = anchorRef.current?.getBoundingClientRect();
    const el = ref.current;
    if (!a || !el) return;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    let left = a.left;
    let top = a.bottom + 6;
    if (left + w > window.innerWidth - 8) left = window.innerWidth - w - 8;
    if (top + h > window.innerHeight - 8) top = Math.max(8, a.top - h - 6);
    setPos({ left: Math.max(8, left), top });
  }, [anchorRef]);

  useEffect(() => {
    const onDown = (e) => {
      if (ref.current?.contains(e.target) || anchorRef.current?.contains(e.target)) return;
      onClose();
    };
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [anchorRef, onClose]);

  const kitColors = kit?.colors || [];
  const inKit = kitColors.some((c) => c.toLowerCase() === value.toLowerCase());
  const eyeDropper = typeof window !== "undefined" && "EyeDropper" in window;

  const pickFromScreen = async () => {
    try {
      const result = await new window.EyeDropper().open();
      onPick(result.sRGBHex);
    } catch {
      /* cancelled */
    }
  };

  return createPortal(
    <div className="cp-popover" ref={ref} style={pos} onMouseDown={(e) => e.stopPropagation()}>
      <div className="cp-section-head">
        <span>{kit?.name || "Brand"} kit</span>
        <button type="button" className="cp-link" onClick={() => setEditing((e) => !e)}>
          {editing ? "Done" : "Edit"}
        </button>
      </div>
      <Swatches
        colors={kitColors}
        value={value}
        onPick={onPick}
        onRemove={editing ? (c) => updateBrandKit({ colors: kitColors.filter((x) => x !== c) }) : null}
      />
      {editing && !inKit && (
        <button
          type="button"
          className="cp-add"
          onClick={() => updateBrandKit({ colors: [...kitColors, value.toUpperCase()] })}
        >
          <span style={{ background: value }} /> Add {value.toUpperCase()} to the kit
        </button>
      )}

      {templateColors.length > 0 && (
        <>
          <div className="cp-section-head"><span>In this template</span></div>
          <Swatches colors={templateColors} value={value} onPick={onPick} />
        </>
      )}

      {recent.length > 0 && (
        <>
          <div className="cp-section-head"><span>Recent</span></div>
          <Swatches colors={recent} value={value} onPick={onPick} />
        </>
      )}

      <div className="cp-section-head"><span>Custom</span></div>
      <div className="cp-custom">
        <input type="color" value={value} onChange={(e) => onPick(e.target.value)} />
        {eyeDropper && (
          <button type="button" className="secondary btn-small" onClick={pickFromScreen} title="Pick a colour from the screen">
            Eyedropper
          </button>
        )}
      </div>
    </div>,
    // Inside the editor root so the scoped editor styles apply (MAS).
    document.querySelector(".at-editor") || document.body
  );
}
